import { DateTime } from "luxon";

import {
  isBenefitSalaryField,
  EMPTY_VALUE_INDICATOR,
  getDisplayValue,
  convertToDateString,
} from "theSource/utils";
import { PrimitiveFieldValue, BenefitSalary, FormField } from "theSource/types";
import { LabelValuePair } from "theSource/components";
import { i18n } from "i18n";

export const getReadonlyField = (field: FormField) => {
  if (isBenefitSalaryField(field.fieldName)) {
    let currentBenefitSalary: BenefitSalary | undefined;

    if (Array.isArray(field.value) && field.value.length > 0) {
      currentBenefitSalary = field.value.reduce(
        (prev: BenefitSalary, current: BenefitSalary) => {
          return prev.orgPlanYearId > current.orgPlanYearId ? prev : current;
        }
      );
    }
    return (
      <>
        <LabelValuePair
          key={field.label}
          label={field.label}
          value={`$ ${currentBenefitSalary?.benefitSalary}`}
        />
      </>
    );
  }

  const displayValue = getDisplayValue(
    field.value as PrimitiveFieldValue,
    field.dataType,
    field.options
  );

  return (
    <LabelValuePair
      greyedOutValue={displayValue === EMPTY_VALUE_INDICATOR}
      key={field.label}
      label={field.label}
      required={field.required}
      value={displayValue}
    />
  );
};

export const getFormField = (
  field: FormField,
  disabled?: boolean,
  customID?: string
) => {
  if (!field.editable) {
    return getReadonlyField(field);
  }
  let id = field.fieldName;
  if (customID) {
    id = `${field.fieldName}-${customID}`;
  }

  const formatDateString = (date: string) => {
    const dateArr = date.split("/");
    return `${dateArr[2]}-${dateArr[0]}-${dateArr[1]}`;
  };

  const defaultRootClass = "mt-n2";

  switch (field.dataType) {
    case "BOOLEAN":
      return (
        <ps-checkbox
          input-id={id}
          key={field.fieldName}
          label={field.label}
          value={field.value}
          name={field.fieldName}
          required={field.required ? true : null}
          checked={field.value ? true : null}
          disabled={disabled ? true : null}
          required-word
          natural
          root-class={
            field["rootClass"] ?? field["extraClass"] ?? defaultRootClass
          }
        ></ps-checkbox>
      );
    case "DATE":
      const fieldValue = convertToDateString(field.value as DateTime);
      const dateValue = formatDateString(fieldValue);
      return (
        <ps-text-field
          type="date"
          input-id={id}
          name={field.fieldName}
          key={field.fieldName}
          label={field.label}
          value={dateValue}
          required={field.required ? true : null}
          validation-text="Field cannot be empty"
          disabled={disabled ? true : null}
          natural
        ></ps-text-field>
      );
    case "NUMBER":
      const isSalaryField = id.includes("salary") || id.includes("pay");
      const iconLeft = true;
      return (
        <ps-numeric-field
          input-id={id}
          name={field.fieldName}
          key={field.fieldName}
          label={field.label}
          value={field.value}
          required={field.required ? true : null}
          disabled={disabled ? true : null}
          validation-text="Field cannot be empty"
          icon={isSalaryField ? "currency-dollar" : undefined}
          {...(isSalaryField ? { iconLeft } : {})}
          natural
        ></ps-numeric-field>
      );
    default:
      if (field.options?.length) {
        const emptyValueLabel = `${i18n.t("common:select")} ${field.label}`;
        const defaultValue =
          field.fieldName === "country" ? "United States" : "";
        const defaultOption = {
          key: "",
          label: emptyValueLabel,
          value: defaultValue,
        };

        const selectProps = {
          labelId: field.label,
          options: [defaultOption, ...field.options],
          required: field.required,
        };
        return (
          <ps-select-field
            input-id={id}
            key={field.fieldName}
            required={field.required ? true : null}
            label={field.label}
            value={field.value === "" ? defaultValue : field.value}
            validation-text="Field cannot be empty"
            disabled={disabled ? true : null}
            custom
          >
            {selectProps.options.map((option) => {
              return (
                <option key={option.key} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </ps-select-field>
        );
      } else if (field.fieldName === "ssn") {
        return (
          <ps-text-field
            type={field.dataType}
            input-id={id}
            name={field.fieldName}
            key={field.fieldName}
            label={field.label}
            value={field.value}
            required={field.required ? true : null}
            validation-text="Must be a valid 9-digit Social Security Number"
            disabled={disabled ? true : null}
            maxlength="11"
            natural
          ></ps-text-field>
        );
      } else if (field.fieldName === "sin") {
        return (
          <ps-text-field
            type={field.dataType}
            input-id={id}
            name={field.fieldName}
            key={field.fieldName}
            label={field.label}
            value={field.value}
            required={field.required ? true : null}
            validation-text="Must be a valid 9-digit Social Identification Number"
            disabled={disabled ? true : null}
            maxlength="11"
            natural
          ></ps-text-field>
        );
      }
      return (
        <ps-text-field
          type={field.fieldName === "email" ? field.fieldName : field.dataType}
          input-id={id}
          name={field.fieldName}
          key={field.fieldName}
          label={field.label}
          value={field.value}
          required={field.required ? true : null}
          disabled={disabled ? true : null}
          validation-text={
            field.fieldName === "email"
              ? "Must be a valid email (example@example.com)"
              : "Field cannot be empty"
          }
          natural
        ></ps-text-field>
      );
  }
};

// validate form fields

export const fields = [
  'input[type="text"]',
  'input[type="number"]',
  'input[type="checkbox"]',
  'input[type="date"]',
  'input[type="email"]',
  "select",
];

export const formatDateString = (date: string) => {
  const dateArr = date?.split("-");
  return `${dateArr[0]}/${dateArr[1]}/${dateArr[2]}`;
};

export const validateFields = (element: any) => {
  if (element.value === "" && element.required === true) {
    element.classList.add("is-invalid");
  } else if (element.value !== "") {
    const regex = new RegExp(element.pattern);
    if (!regex.test(element.value)) {
      element.classList.add("is-invalid");
    } else {
      element.classList.remove("is-invalid");
    }
  } else {
    element.classList.remove("is-invalid");
  }
};

export const validateForm = () => {
  const inputFields = fields.map((field) => document.querySelectorAll(field));
  inputFields.forEach((inputField) => {
    inputField.forEach((field) => {
      validateFields(field);
    });
  });
};
